/**
 * Admin Launchpad Health Page
 *
 * Real-time monitoring of launchpad service health:
 * - XRPL connection status
 * - Mint processor state and circuit breaker
 * - Mint pipeline throughput
 * - Settlement queue health
 * - Recent error patterns
 */

import { useEffect, useState } from 'react';
import { AdminLayout } from '../components/AdminLayout.js';
import { LoadingSpinner } from '../components/ui/LoadingSpinner.js';
import { UnmatchedPaymentsCard } from '../components/admin/UnmatchedPaymentsCard.js';
import { getLaunchpadHealth, type LaunchpadHealthData } from '../services/adminApi.js';

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    healthy: 'bg-green-100 text-green-800',
    degraded: 'bg-yellow-100 text-yellow-800',
    critical: 'bg-red-100 text-red-800',
    true: 'bg-green-100 text-green-800',
    false: 'bg-red-100 text-red-800',
  };
  return (
    <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${styles[status] || 'bg-gray-100 text-gray-800'}`}>
      {status}
    </span>
  );
}

function StatBox({ label, value, variant = 'default' }: { label: string; value: string | number; variant?: 'default' | 'success' | 'warning' | 'error' }) {
  const bg = { default: 'bg-gray-50', success: 'bg-green-50', warning: 'bg-yellow-50', error: 'bg-red-50' };
  const text = { default: 'text-gray-900', success: 'text-green-700', warning: 'text-yellow-700', error: 'text-red-700' };
  return (
    <div className={`rounded-md ${bg[variant]} p-3 text-center`}>
      <p className={`text-xl font-bold ${text[variant]}`}>{value}</p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
}

function timeAgo(isoString: string | null): string {
  if (!isoString) return 'never';
  const diff = Date.now() - new Date(isoString).getTime();
  if (diff < 60000) return `${Math.round(diff / 1000)}s ago`;
  if (diff < 3600000) return `${Math.round(diff / 60000)}m ago`;
  return `${Math.round(diff / 3600000)}h ago`;
}

export function AdminLaunchpadHealthPage() {
  const [data, setData] = useState<LaunchpadHealthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const loadData = async () => {
    try {
      setError(null);
      const result = await getLaunchpadHealth();
      setData(result);
      setLastRefresh(new Date());
    } catch (err: any) {
      setError(err.message || 'Failed to load health data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, 10000);
    return () => clearInterval(interval);
  }, []);

  if (loading && !data) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-16">
          <LoadingSpinner size="md" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="mx-auto max-w-7xl">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Launchpad Health</h1>
            <p className="text-sm text-gray-500">
              Auto-refreshes every 10s{lastRefresh ? ` | Last: ${lastRefresh.toLocaleTimeString()}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {data && <StatusBadge status={data.overallStatus} />}
            <button onClick={loadData} className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-200">
              Refresh
            </button>
          </div>
        </div>

        {error && (
          <div className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {data && (
          <>
            {/* Service Status Cards */}
            <div className="mb-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
              {/* Launchpad Service */}
              <div className="rounded-lg border border-gray-200 bg-white p-5">
                <h3 className="mb-3 text-sm font-semibold uppercase text-gray-500">Service</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Reachable</span>
                    <StatusBadge status={String(data.serviceReachable)} />
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Database</span>
                    <StatusBadge status={String(data.service?.checks?.database ?? false)} />
                  </div>
                </div>
              </div>

              {/* XRPL Connection */}
              <div className="rounded-lg border border-gray-200 bg-white p-5">
                <h3 className="mb-3 text-sm font-semibold uppercase text-gray-500">XRPL Connection</h3>
                {data.service?.checks?.xrpl ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Connected</span>
                      <StatusBadge status={String(data.service.checks.xrpl.connected)} />
                    </div>
                    {data.service.checks.xrpl.reconnecting && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Reconnecting</span>
                        <span className="text-yellow-600">attempt {data.service.checks.xrpl.reconnectAttempts}</span>
                      </div>
                    )}
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last Connected</span>
                      <span className="text-gray-900">{timeAgo(data.service.checks.xrpl.lastConnectedAt)}</span>
                    </div>
                    {data.service.checks.xrpl.lastDisconnectedAt && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Last Disconnected</span>
                        <span className="text-gray-900">{timeAgo(data.service.checks.xrpl.lastDisconnectedAt)}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Service unreachable</p>
                )}
              </div>

              {/* Mint Processor */}
              <div className="rounded-lg border border-gray-200 bg-white p-5">
                <h3 className="mb-3 text-sm font-semibold uppercase text-gray-500">Mint Processor</h3>
                {data.service?.checks?.mintProcessor ? (
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Running</span>
                      <StatusBadge status={String(data.service.checks.mintProcessor.running)} />
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Circuit Breaker</span>
                      <StatusBadge status={data.service.checks.mintProcessor.circuitBreakerOpen ? 'critical' : 'healthy'} />
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Workers</span>
                      <span className="text-gray-900">{data.service.checks.mintProcessor.activeWorkers}/{data.service.checks.mintProcessor.maxConcurrency}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Poll Errors</span>
                      <span className={data.service.checks.mintProcessor.consecutivePollErrors > 0 ? 'text-red-600 font-medium' : 'text-gray-900'}>
                        {data.service.checks.mintProcessor.consecutivePollErrors}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Last Success</span>
                      <span className="text-gray-900">{timeAgo(data.service.checks.mintProcessor.lastSuccessfulPollAt)}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-gray-400">Service unreachable</p>
                )}
              </div>
            </div>

            {/* Mint Pipeline */}
            <div className="mb-6 rounded-lg border border-gray-200 bg-white p-5">
              <h3 className="mb-4 text-sm font-semibold uppercase text-gray-500">Mint Pipeline</h3>
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-5 lg:grid-cols-9">
                <StatBox label="Payment Pending" value={data.pipeline.paymentPending} variant={data.pipeline.paymentPending > 10 ? 'warning' : 'default'} />
                <StatBox label="Confirmed" value={data.pipeline.paymentConfirmed} />
                <StatBox label="Minting" value={data.pipeline.minting} />
                <StatBox label="Minted" value={data.pipeline.minted} />
                <StatBox label="Transferring" value={data.pipeline.transferring} />
                <StatBox label="Completed (24h)" value={data.pipeline.completed24h} variant="success" />
                <StatBox label="Failed (24h)" value={data.pipeline.failed24h} variant={data.pipeline.failed24h > 0 ? 'error' : 'default'} />
                <StatBox label="Expired (24h)" value={data.pipeline.expired24h} variant={data.pipeline.expired24h > 0 ? 'warning' : 'default'} />
                <StatBox label="Stuck" value={data.pipeline.stuckMints} variant={data.pipeline.stuckMints > 0 ? 'error' : 'default'} />
              </div>
              {data.pipeline.avgProcessingSeconds !== null && (
                <p className="mt-3 text-xs text-gray-500">
                  Avg processing time (24h): {data.pipeline.avgProcessingSeconds}s
                </p>
              )}
            </div>

            {/* Bottom row: Settlements + Recent Errors */}
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
              {/* Settlements */}
              <div className="rounded-lg border border-gray-200 bg-white p-5">
                <h3 className="mb-3 text-sm font-semibold uppercase text-gray-500">Settlements</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pending</span>
                    <span className={data.settlements.pending > 0 ? 'font-medium text-yellow-600' : 'text-gray-900'}>{data.settlements.pending}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Failed</span>
                    <span className={data.settlements.failed > 0 ? 'font-medium text-red-600' : 'text-gray-900'}>{data.settlements.failed}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Last Completed</span>
                    <span className="text-gray-900">{timeAgo(data.settlements.lastCompletedAt)}</span>
                  </div>
                </div>
              </div>

              {/* Recent Errors */}
              <div className="rounded-lg border border-gray-200 bg-white p-5">
                <h3 className="mb-3 text-sm font-semibold uppercase text-gray-500">Recent Errors (1h)</h3>
                {data.recentErrors.length > 0 ? (
                  <div className="space-y-2">
                    {data.recentErrors.map((err, i) => (
                      <div key={i} className="flex items-start justify-between rounded bg-red-50 p-2 text-sm">
                        <span className="text-red-700 break-all">{err.message}</span>
                        <span className="ml-2 shrink-0 rounded bg-red-100 px-1.5 py-0.5 text-xs font-medium text-red-800">{err.count}x</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-green-600">No errors in the last hour</p>
                )}
              </div>
            </div>

            {/* Unmatched Payments (full-width) */}
            <div className="mt-6">
              <UnmatchedPaymentsCard />
            </div>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
